import ChevronLeftIcon from "@redotech/redo-icons/chevron-left_filled.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right_filled.svg";
import CircleIcon from "@redotech/redo-icons/circle_filled.svg";
import classNames from "classnames";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { memo, ReactNode, useMemo, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import * as carouselCss from "./carousel.module.css";
import { Flex } from "./flex";
import { StyleProps } from "./styles";
import { SpacingValue } from "./theme/box";
import { spacingPx } from "./theme/spacing";

interface CarouselProps extends Omit<StyleProps, "gap"> {
  children: ReactNode[];
  nextSlideLabel?: string;
  numberVisible?: number;
  previousSlideLabel?: string;
  showOverflow?: boolean;
  slidePositionLabel?: (position: number, count: number) => string;
  /** The gap between the carousel and the page dots */
  verticalGap?: SpacingValue;
  showArrows?: boolean;
  gap?: SpacingValue;
  showVignette?: boolean;
  /** Show arrows only on hover near edges, hidden when at start/end */
  hoverArrows?: boolean;
}

/** Uses keen slider: https://keen-slider.io
 * If you need to extend it, check for examples of what you want to do at https://keen-slider.io/examples
 */
export const Carousel = memo(function Carousel({
  children,
  nextSlideLabel = "Next slide",
  numberVisible,
  previousSlideLabel = "Previous slide",
  showOverflow,
  slidePositionLabel = (position, count) =>
    `Go to slide ${position} of ${count}`,
  gap,
  verticalGap,
  showArrows,
  showVignette,
  hoverArrows,
  ...rest
}: CarouselProps) {
  const visible = numberVisible || 2.2;
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      selector: ".keen-slider__slide",
      loop: false,
      mode: "free-snap",
      slides: {
        perView: visible,
        spacing: gap ? spacingPx(gap) : spacingPx("md"),
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    },
    [],
  );

  const atStart = currentSlide === 0;
  const atEnd =
    loaded && instanceRef.current
      ? currentSlide >=
        instanceRef.current.track.details.slides.length - Math.floor(visible)
      : false;
  const pageCount =
    loaded && instanceRef.current
      ? Math.max(
          instanceRef.current.track.details.slides.length -
            Math.floor(visible) +
            1,
          0,
        )
      : 0;

  const vignetteClass = useMemo(() => {
    if (!showVignette || !loaded) {
      return undefined;
    }
    if (atStart && atEnd) {
      return undefined;
    }
    if (atStart) {
      return carouselCss.vignetteRight;
    }
    if (atEnd) {
      return carouselCss.vignetteLeft;
    }
    return carouselCss.vignetteBoth;
  }, [showVignette, loaded, atStart, atEnd]);
  const pageDots =
    pageCount > 1 ? (
      <Flex dir="row" gap="none">
        {[...Array(pageCount).keys()].map((idx) => {
          return (
            <RedoButton
              aria-current={currentSlide === idx}
              aria-label={slidePositionLabel(idx + 1, pageCount)}
              buttonClassName={classNames(
                carouselCss.dot,
                currentSlide === idx && carouselCss.active,
              )}
              className={carouselCss.dotContainer}
              hierarchy="tertiary"
              IconLeading={CircleIcon}
              key={idx}
              onClick={() => {
                instanceRef.current?.moveToIdx(idx);
              }}
              size="xs"
            />
          );
        })}
      </Flex>
    ) : undefined;
  const showBottomControls =
    loaded && instanceRef.current && showArrows && !hoverArrows;

  return (
    <Flex
      className={
        showOverflow
          ? carouselCss.containerVisibleOverflow
          : carouselCss.container
      }
      dir="column"
      gap={verticalGap}
    >
      <Flex
        className={
          showOverflow
            ? carouselCss.sliderContainerVisibleOverflow
            : carouselCss.sliderContainer
        }
        gap="none"
      >
        <Flex
          className={classNames(
            "keen-slider",
            vignetteClass,
            showOverflow && carouselCss.sliderOverflow,
          )}
          ref={sliderRef}
          {...rest}
          gap="none"
        >
          {children.map((child, index) => (
            <Flex
              className={classNames("keen-slider__slide", carouselCss.slide)}
              key={index}
            >
              {child}
            </Flex>
          ))}
        </Flex>
        {loaded && instanceRef.current && hoverArrows && (
          <RedoButton
            aria-disabled={atStart}
            aria-label={previousSlideLabel}
            buttonClassName={carouselCss.hoverArrowButton}
            className={carouselCss.leftHoverArrowContainer}
            data-web="Carousel"
            hierarchy="secondary"
            IconLeading={ChevronLeftIcon}
            onClick={() => {
              if (!atStart) {
                instanceRef.current?.prev();
              }
            }}
            size="xl"
            tabIndex={atStart ? -1 : 0}
          />
        )}
        {loaded && instanceRef.current && hoverArrows && (
          <RedoButton
            aria-disabled={atEnd}
            aria-label={nextSlideLabel}
            buttonClassName={carouselCss.hoverArrowButton}
            className={carouselCss.rightHoverArrowContainer}
            hierarchy="secondary"
            IconLeading={ChevronRightIcon}
            onClick={() => {
              if (!atEnd) {
                instanceRef.current?.next();
              }
            }}
            size="xl"
            tabIndex={atEnd ? -1 : 0}
          />
        )}
      </Flex>
      {showBottomControls ? (
        <Flex
          align="center"
          aria-label="Carousel controls"
          gap="xs"
          role="group"
        >
          <RedoButton
            aria-label={previousSlideLabel}
            className={carouselCss.compactArrow}
            disabled={atStart}
            hierarchy="secondary"
            IconLeading={ChevronLeftIcon}
            onClick={() => {
              instanceRef.current?.prev();
            }}
            size="sm"
          />
          <RedoButton
            aria-label={nextSlideLabel}
            className={carouselCss.compactArrow}
            disabled={atEnd}
            hierarchy="secondary"
            IconLeading={ChevronRightIcon}
            onClick={() => {
              instanceRef.current?.next();
            }}
            size="sm"
          />
          {pageDots}
        </Flex>
      ) : (
        pageDots && (
          <Flex gap="none" justify="center">
            {pageDots}
          </Flex>
        )
      )}
    </Flex>
  );
});
